import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
	Button,
	Checkbox,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	ListItemIcon,
	ListItemText,
	Icon,
	IconButton,
	Menu,
	MenuItem,
	MenuList,
	TableCell,
	TableHead,
	TableRow,
	TableSortLabel,
	Tooltip,
	Typography
} from '@material-ui/core';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/styles';

import * as Actions from './store/actions';
import * as ActionsDialog from 'app/store/actions';

import Error from 'app/fuse-layouts/shared-components/Error';

const rows = [
	{
		id: 'image',
		align: 'left',
		disablePadding: false,
		label: '',
		sort: false
	},
	{
		id: 'name',
		align: 'left',
		disablePadding: false,
		label: 'Name',
		sort: true
	},
	{
		id: 'instanceType',
		align: 'left',
		disablePadding: false,
		label: 'Instance Type',
		sort: true
	},
	{
		id: 'state',
		align: 'left',
		disablePadding: false,
		label: 'State',
		sort: true
	},
	{
		id: 'cores',
		align: 'center',
		disablePadding: false,
		label: 'Cores / Threads',
		sort: true
	},

	{
		id: 'loadAvg',
		align: 'center',
		disablePadding: false,
		label: 'Load Avg.',
		sort: true
	}
];

const useStyles = makeStyles(() => ({
	actionsButtonWrapper: {
		background: 'rgb(250,250,250)'
	}
}));

const NodesTableHead = props => {
	const dispatch = useDispatch();
	const classes = useStyles(props);

	const open = useSelector(({ fuse }) => fuse.dialog.state);
	const cluster = useSelector(({ clusters }) => clusters.cluster.cluster);

	const [checked, setChecked] = useState(false);
	const [selectedMenu, setSelectedMenu] = useState(null);

	useEffect(() => {
		if (open && selectedMenu) {
			closeSelectedMenu();
		}
	}, [open, selectedMenu]);

	useEffect(() => {
		if (props.numSelected === 0 && props.rowCount === 0) {
			setChecked(false);
		} else if (props.numSelected === props.rowCount) {
			setChecked(true);
		} else {
			setChecked(false);
		}
	}, [props]);

	const createSortHandler = property => event => props.onRequestSort(event, property);

	const openSelectedMenu = event => setSelectedMenu(event.currentTarget);

	const closeSelectedMenu = () => setSelectedMenu(null);

	const handleTransition = (selected, type) => {
		const action =
			type === 'deploy'
				? dispatch(Actions.deployNode(cluster.id, selected))
				: dispatch(Actions.destroyNode(cluster.id, selected));
		action.catch(err =>
			dispatch(
				ActionsDialog.openDialog({
					children: <Error message={`${err.data.message}! Refer to logs...`} />,
					maxWidth: 'xs',
					fullWidth: true,
					onClose: (event, reason) => {
						if (reason !== 'backdropClick') {
							dispatch(ActionsDialog.closeDialog());
						}
					}
				})
			)
		);
		closeSelectedMenu();
		dispatch(ActionsDialog.closeDialog());
		props.resetSelected();
	};

	const showConfirmation = selected => {
		dispatch(
			ActionsDialog.openDialog({
				children: (
					<React.Fragment>
						<DialogTitle>WARNING!</DialogTitle>
						<DialogContent>
							<DialogContentText id="nodes_table_head_confirmation-msg">
								Any node that is running a job will Are you sure you want to destroy?
							</DialogContentText>
						</DialogContent>
						<DialogActions>
							<Button id="nodes_table_head_confirmation-back-btn" onClick={() => dispatch(ActionsDialog.closeDialog())} color="primary">
								Back
							</Button>
							<Button
								id="nodes_table_head_confirmation-confirm-btn"
								onClick={() => handleTransition(selected, 'destroy')}
								color="primary"
								autoFocus
							>
								Destroy
							</Button>
						</DialogActions>
					</React.Fragment>
				),
				maxWidth: 'sm',
				fullWidth: true,
				onClose: (event, reason) => {
					if (reason !== 'backdropClick') {
						dispatch(ActionsDialog.closeDialog());
					}
				}
			})
		);
		props.resetSelected();
	};

	return (
		<TableHead>
			<TableRow className="h-64">
				<TableCell id="nodes_table_head_node-header-actions" padding="checkbox" className=" pl-4 sm:pl-12 z-99">
					<Checkbox
						id="nodes_table_head_node-header-check-box"
						indeterminate={props.numSelected > 0 && props.numSelected < props.rowCount}
						checked={checked}
						onChange={props.onSelectAllClick}
					/>
					{props.numSelected > 0 && (
						<div
							className={clsx(
								'flex items-center justify-center absolute w-64 top-0 left-0 ml-64 h-60 z-10',
								classes.actionsButtonWrapper
							)}
						>
							<IconButton
								id="nodes_table_head_actions-more-btn"
								className="cursor-pointer"
								aria-owns={selectedMenu ? 'selectedMenu' : null}
								aria-haspopup="true"
								onClick={openSelectedMenu}
							>
								<Icon>more_horiz</Icon>
							</IconButton>
							<Menu id="nodes_table_head_selectedMenu" anchorEl={selectedMenu} open={Boolean(selectedMenu)} onClose={closeSelectedMenu}>
								<MenuList>
									<MenuItem
										id="nodes_table_head_actions-more-deploy"
										disabled={cluster.info.bursting.type !== 'none'}
										onClick={() => handleTransition(props.selected, 'deploy')}
									>
										<ListItemIcon className="min-w-40">
											<Icon>cloud_upload</Icon>
										</ListItemIcon>
										<ListItemText primary="Deploy" />
									</MenuItem>
									<MenuItem
										id="nodes_table_head_actions-more-destroy"
										disabled={cluster.info.bursting.type !== 'none'}
										onClick={() => showConfirmation(props.selected)}
									>
										<ListItemIcon className="min-w-40">
											<Icon>cloud_download</Icon>
										</ListItemIcon>
										<ListItemText primary="Destroy" />
									</MenuItem>
								</MenuList>
							</Menu>
						</div>
					)}
				</TableCell>
				{rows.map(row => {
					return (
						<TableCell
							key={row.id}
							id={row.id}
							align={row.align}
							padding={row.disablePadding ? 'none' : 'normal'}
							sortDirection={props.order.id === row.id ? props.order.direction : false}
						>
							{row.sort && (
								<Tooltip
									title="Sort"
									placement={row.align === 'right' ? 'bottom-end' : 'bottom-start'}
									enterDelay={300}
								>
									<TableSortLabel
										id={`table-header-${row.id}`}
										active={props.order.id === row.id}
										direction={props.order.direction}
										onClick={createSortHandler(row.id)}
									>
										<Typography className="font-bold">{row.label}</Typography>
									</TableSortLabel>
								</Tooltip>
							)}
						</TableCell>
					);
				}, this)}
				<TableCell 
					id="nodes_table_head_actions" 
					align="center" 
					padding="normal"
					style={{ 
						position: 'sticky',
						right: 0,
						backgroundColor: '#fafafa',
						zIndex: 2
					}}
				>
					<Typography className="font-bold"> Actions</Typography>
				</TableCell>
			</TableRow>
		</TableHead>
	);
};

export default NodesTableHead;
